import React from 'react'
import {
  Block,
  ButtonGroup,
  Button,
  Alert,
  H6,
  P,
  TextArea,
  RadioButton,
  RadioGroup,
  Text
} from '@mote/ui'
import { validEmail } from '../../shared/Utils'
import { useAccountAddUsersMutation } from '@mote/api'
import { Content } from '../Dashboard/components'

export const AccountAddUsers = ({
  account,
  onCancel,
  onSuccess,
  fulfilledSeats,
  onAccountUpdated
}) => {
  const [localAccountState, setLocalAccountState] = React.useState(account)
  const [emailAddresses, setEmailAddresses] = React.useState('')
  const [error, setError] = React.useState(null)
  const {
    mutate,
    loading,
    error: addUsersError,
    fetched,
    flush
  } = useAccountAddUsersMutation()

  const multiplePlans = React.useMemo(() => {
    const { canAddEssential, canAddUnlimited } =
      localAccountState.currentLicense

    return canAddEssential && canAddUnlimited
  }, [localAccountState])

  // ch6003 - attempt #2
  const [selectedPlan, setSelectedPlan] = React.useState(
    multiplePlans
      ? null
      : localAccountState?.currentLicense?.canAddEssential
      ? 'essential'
      : 'unlimited',
    [localAccountState]
  )

  // const [selectedPlan, setSelectedPlan] = React.useState(
  //   multiplePlans ? null : 'unlimited'
  // )

  const results = React.useMemo(() => {
    const resultsObj = { success: null, errors: [] }

    if (!fetched) return resultsObj

    const ageGroupUpdates = localAccountState.ageGroupUpdates || []

    const addedUsers = localAccountState.added
      ?.concat(localAccountState.upgrades)
      .concat(localAccountState.downgrades)
      .concat(ageGroupUpdates)

    if (addedUsers?.length > 0) {
      resultsObj.success = (
        <Alert
          type='success'
          message={`Added ${addedUsers.length} user
        ${
          addedUsers.length > 1 ? 's' : ''
        } to your plan: ${localAccountState.added.join(', ')}`}
          style={{ margin: 0 }}
        />
      )
    }

    if (localAccountState.invalidEmails?.length > 0) {
      resultsObj.errors.push(
        <Text color='#F44336' tag='small'>
          These addresses are not valid:{' '}
          {localAccountState.invalidEmails.join(', ')}
        </Text>
      )
    }

    if (localAccountState.ignoredDuplicates?.length > 0) {
      resultsObj.errors.push(
        <Text color='#F44336' tag='small'>
          These addresses are already attached to your organization:{' '}
          {localAccountState.ignoredDuplicates.join(', ')}
        </Text>
      )
    }

    if (localAccountState.overTheLimit?.length > 0) {
      resultsObj.errors.push(
        <Text color='#F44336' tag='small'>
          You have run out of available licenses, we couldn't add these:{' '}
          {localAccountState.overTheLimit.join(', ')}
        </Text>
      )
    }

    if (localAccountState.otherErrors?.length > 0) {
      resultsObj.errors.push(
        <Text color='#F44336' tag='small'>
          For reasons beyond your control, we couldn't add the following
          address. If the problem persists, content support@mote.com:{' '}
          {localAccountState.otherErrors.join(', ')}
        </Text>
      )
    }

    return resultsObj
  }, [fetched, localAccountState])

  React.useEffect(() => {
    if (results.success && results.errors.length) return

    if (results.success) {
      onSuccess(results.success)
    }
  }, [results, onSuccess])

  function handleEmailAddressUpdate(event) {
    setEmailAddresses(event.target.value)
  }

  function handlePlanSelection(plan) {
    setSelectedPlan(plan)
  }

  async function handleAddUsers() {
    flush()
    setError(null)
    const emails = emailAddresses
      .trim() // cleanup
      .replace(/(\s*,?\s*)*$/, '') // cleanup
      .replace(/\s+/g, ',') // format
      .replace(/,,/g, ',') // format - should be after cleanup
      .split(',')

    if (emails.length > 20) {
      setError(
        `You can upload 20 email addresses at a time. You should trim the above list from "${emails[20]}"`
      )
      return
    }

    for (let i = 0; i < emails.length; i++) {
      if (validEmail(emails[i])) continue
      setError(
        `At least one item above is not a valid email address - eg '${emails[i]}' - please correct before proceeding`
      )

      return
    }

    const res = await mutate({
      variables: {
        emailAddresses: emails,
        productEnum: selectedPlan,
        forceMinor: false
      }
    })

    if (res.data) {
      setLocalAccountState(res.data)
      onAccountUpdated(res.data)
    }
  }

  if (fulfilledSeats) {
    return (
      <Block maxWidth={760}>
        <Content type='section' title='Adding users:' noPadding>
          <Alert
            type='warn'
            message='You have used all your purchased licenses, contact support@mote.com
            to add more or reassign seats. More self-service features coming
            soon!'
          />
        </Content>
      </Block>
    )
  }

  return (
    <Block maxWidth={700}>
      <Content type='section' title='Adding users:' noPadding>
        <Block gap={8}>
          {!multiplePlans && (
            <H6
              fontSize={15}
              fontWeight={500}
              lineHeight='140%'
              color='#25252D'
            >
              Plan: {localAccountState.institutionProductSummary}
            </H6>
          )}

          {multiplePlans && (
            <Block margin='0 0 32px 0' gap={20}>
              <H6
                fontSize={15}
                fontWeight={500}
                lineHeight='140%'
                color='#25252D'
              >
                Pick a plan:
              </H6>

              <RadioGroup
                value={selectedPlan}
                onChange={handlePlanSelection}
                gap={10}
              >
                <RadioButton value='unlimited' label='Unlimited' />
                <RadioButton value='essential' label='Essential' />
              </RadioGroup>
            </Block>
          )}

          {selectedPlan && (
            <P fontSize={15} fontWeight={400} lineHeight='120%' color='#6C6C84'>
              Copy and paste up to 20 email addresses of users at a time. For
              larger groups, we recommend using the CSV import tool.
            </P>
          )}
        </Block>

        {selectedPlan && (
          <Block gap={16}>
            <TextArea
              value={emailAddresses}
              invalid={results.errors.length}
              onChange={handleEmailAddressUpdate}
            />

            {error && <Alert type='error' message={error} />}
            {results?.errors.map((error, index) => (
              <React.Fragment key={index}>{error}</React.Fragment>
            ))}
            {addUsersError && (
              <Alert
                type='error'
                message='An unknown error occurred. Please try again and if the problem persists, kindly contact support at support@mote.com'
              />
            )}
          </Block>
        )}

        <ButtonGroup buttonGap={20}>
          <Button
            text='Add users'
            onClick={handleAddUsers}
            loading={loading}
            disabled={loading || !emailAddresses || !selectedPlan}
          />
          <Button variant='secondary' text='Cancel' onClick={onCancel} />
        </ButtonGroup>

        {results.success && <Block>{results.success}</Block>}
      </Content>
    </Block>
  )
}
